<template>
  <div class="planning pb-32">
    <header class="inner-head">
      <div class="flex justify-between">
        <h2 class="inner-head-title">Long Term Planning</h2>
        <img
          v-if="currentUser.isSecondaryBased"
          @click="
            openInNewTab(
              'https://www.pescholar.com/insight/the-primary-physical-education-concept-curriculum/'
            )
          "
          class="pl-2 w-2/12 object-scale-down"
          src="/static/images/pescholar-collab.png"
          alt=""
        />
      </div>
    </header>

    <div v-if="hasExtendedLayout" class="header-subnav-holder">
      <button
        v-for="term in termDropdown"
        :key="term.key"
        type="button"
        class="header-subnav focus:outline-none"
        @click="selectTerm(term)"
        v-bind:class="[termSelected.key == term.key ? 'selected' : '']"
      >
        {{ term.name }}
      </button>
    </div>

    <FilterBar :nosearch="true">
      <template v-slot:filter>
        <div>
          <Dropdown
            :options="catDropdown"
            :selected="catSelected"
            v-on:updateOption="updateCatValue"
            style="min-width: 14rem"
          ></Dropdown>
        </div>

        <div v-if="hasExtendedLayout">
          <Dropdown
            :options="roomDropdown"
            :selected="roomSelected"
            v-on:updateOption="updateRoom"
            style="min-width: 12rem"
          ></Dropdown>
        </div>

        <div v-if="hasExtendedLayout">
          <Dropdown
            :options="teacherDropdown"
            :selected="teacherSelected"
            v-on:updateOption="updateTeacher"
            style="min-width: 12rem"
          ></Dropdown>
        </div>
      </template>
      <template v-slot:actions>
        <div>
          <button
            type="button"
            class="filter-bar-button hover:opacity-75 focus:outline-none"
            @click="generatePrint()"
          >
            <i class="fas fa-share-alt"></i>
            Export
          </button>
        </div>
        <div>
          <div
            v-if="isPlanningLocked"
            class="text-red-500 filter-bar-button p-2"
          >
            Planning locked
          </div>
          <button
            v-else
            type="button"
            class="filter-bar-button text-gray-800 hover:opacity-75 focus:outline-none"
            v-bind:class="inEditMode ? 'bg-gray-500 ' : ''"
            @click="editGrid()"
          >
            <i class="fas fa-minus-square"></i>
            Change Planning
          </button>
        </div>
        <div v-if="1 === 3">
          <button
            type="button"
            class="action-button hover:opacity-75"
            @click="editGrid()"
          >
            <i class="fas fa-plus"></i>
            Add New
          </button>
        </div>
      </template></FilterBar
    >

    <div class="pt-3">
      <planningList v-if="isMobile"></planningList>

      <div v-else id="printable">
        <planningGrid
          :editMode="inEditMode"
          :termsFiltered="termsInView"
          :teacherFiltered="teacherSelected"
          :categoryFiltered="catSelected"
          :roomFiltered="roomSelected"
        ></planningGrid>
      </div>
    </div>

    <div style="display: none !important">
      <vue-html2pdf
        :show-layout="false"
        :float-layout="false"
        :enable-download="true"
        :preview-modal="false"
        :paginate-elements-by-height="1400"
        :filename="printingFilename"
        :pdf-quality="2"
        :manual-pagination="true"
        pdf-format="a4"
        pdf-orientation="landscape"
        pdf-content-width="100%"
        ref="html2PdfPlanning"
      >
        <section slot="pdf-content">
          <div class="bg-pepa-red mb-8">
            <div
              class="flex justify-center p-2 space-x-12 mx-auto w-1/2 text-white"
            >
              <div class="text-center">
                <h1 class="text-3xl font-bold pb-3">Long Term Planning</h1>
                <div class="h-1 bg-white block w-full">&nbsp;</div>
                <h2 class="text-2xl font-bold pb-3">{{ printTitle }}</h2>
              </div>
              <div>
                <img
                  src="/static/images/print_logo.png"
                  height="110px"
                  width="110px"
                />
              </div>
            </div>
          </div>

          <planningGrid
            :editMode="inEditMode"
            :termsFiltered="termsInView"
            :teacherFiltered="teacherSelected"
            :categoryFiltered="catSelected"
            :roomFiltered="roomSelected"
          ></planningGrid>

          <div
            class="bg-pepa-red mb-8 p-2 px-6 text-right text-white text-xs pb-4 flex justify-between"
          >
            <div>Long Term Planning - {{ printTitle }}</div>
            <div>&copy; PE Passport Ltd</div>
          </div>
        </section>
      </vue-html2pdf>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import VueHtml2pdf from "vue-html2pdf";

import { SchoolService, BaseService } from "@/services/school.service";

import { calendarYearList, termList } from "@/assets/js/utils";
import { roomMixin } from "@/mixins/roomMixin.js";

const Dropdown = () => import("@/components/Dropdown.vue");
const planningGrid = () => import("@/components/planning/ThePlanningGrid.vue");
const planningList = () => import("@/components/planning/ThePlanningList.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");

export default {
  name: "planning",
  mixins: [roomMixin],
  props: [],
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
      settings: "settings",
    }),
    printTitle() {
      let calendarYear = calendarYearList().find((y) => y.current === true);
      return this.currentUser.schoolName + " " + calendarYear.short;
    },
    printingFilename() {
      if (this.currentUser.schoolName != undefined) {
        let calendarYear = calendarYearList().find((y) => y.current === true);
        let name =
          this.currentUser.schoolName.replace(" ", "_") +
          "_long_term_plan_" +
          calendarYear.short;
        return name.toLowerCase();
      }
    },
    isMobile() {
      return false; // TOFIX: mobile view //screen.width < 994;
    },
    calendarDropdown() {
      return this.calendarList;
    },
    isPlanningLocked() {
      return this.settings.planningLocked || this.currentUser.isOnTrial;
    },
    catDropdown() {
      var results = [{ id: 0, name: "All Categories" }].concat(this.cats);
      this.catSelected = results[0];
      return results;
    },
    termDropdown() {
      var results = this.terms;
      this.termSelected = results[0];
      return results;
    },
    hasExtendedLayout() {
      return this.currentUser.isSecondaryBased;
    },
    teacherDropdown() {
      let results = [{ id: 0, name: "Select a teacher" }].concat(this.teachers);
      if (this.teacherSelected === undefined) {
        this.teacherSelected = results[0];
      }
      return results;
    },
    roomDropdown() {
      let roomList = this.schoolRooms.map(function (item, index) {
        return { id: item.identifier, name: item.title };
      });

      let results = [{ id: 0, name: "Select a room" }].concat(roomList);
      if (this.roomSelected === undefined) {
        this.roomSelected = results[0];
      }
      return results;
    },
    termsInView() {
      let term = this.termSelected;
      if (term === undefined) {
        return this.terms;
      } else {
        return [term];
      }
    },
  },
  data() {
    return {
      calendarList: calendarYearList(),
      selectedCalendar: {},
      inEditMode: false,
      terms: termList(),
      cats: [],
      termSelected: undefined,
      teacherSelected: undefined,
      teachers: [],
      roomSelected: undefined,
      catSelected: {},
    };
  },
  created() {
    this.selectedCalendar = this.calendarList[0];
    // cats
    BaseService.getAll("categories")
      .then((response) => (this.cats = response.data.data))
      .catch((res) => {
        console.log(res);
      });

    // users
    SchoolService.getAll("users")
      .then((response) => (this.teachers = response.data.data))
      .catch((err) => {
        console.log("users " + err);
        this.errored = true;
      });
  },
  mounted() {},
  methods: {
    editGrid() {
      this.inEditMode = !this.inEditMode;
      // this.$router.push({ name: "PlanningEdit" });
      // window.scrollTo(0, 0);
    },
    selectTerm(term) {
      this.termSelected = term;
    },
    updateTeacher(value) {
      this.teacherSelected = value;
    },
    updateRoom(value) {
      this.roomSelected = value;
    },
    updateCatValue(cat) {
      this.catSelected = cat;
    },
    generatePrint() {
      this.$refs.html2PdfPlanning.generatePdf();
    },
  },
  components: {
    FilterBar,
    planningGrid,
    planningList,
    Dropdown,
    VueHtml2pdf,
  },
};
</script>

<style scoped>
</style>
